import { Box3, Group, Vector3 } from "three";
import { type ModelAssetLoader, modelAssetLibrary } from "../models/model-asset-library";
import { ModelAsset } from "../models/model-assets";

export type DoubleChainConveyorOptions = {
  crossbarZInset?: number;
  length?: number;
  position?: { x?: number; y?: number; z?: number };
  rotationY?: number;
  sideRailEndInset?: number;
  sideRailXInset?: number;
  sideRailYOffset?: number;
  wheelXOutset?: number;
  width?: number;
};

type AlignedPart = {
  content: Group;
  object: Group;
  size: Vector3;
};

const defaultOptions = {
  crossbarZInset: 0.004,
  length: 1.701,
  rotationY: 0,
  sideRailEndInset: 0.02,
  sideRailXInset: 0.156,
  sideRailYOffset: 0.26,
  wheelXOutset: 0.06,
  width: 1.3314
};
const backPostRotationY = 0;
const frontPostRotationY = Math.PI;

export class DoubleChainConveyor extends Group {
  private crossbarBaseZ = 0;
  private crossbarZInset = 0;
  private readonly length: number;
  private readonly requestedSideRailYOffset?: number;
  private readonly structure = new Group();
  private sideRailBaseX = 0;
  private sideRailEndInset = 0;
  private sideRailOriginalLength = 0;
  private sideRailXInset = 0;
  private sideRailYOffset = 0;
  private wheelHalfWidth = 0;
  private wheelXOutset = 0;
  private readonly width: number;

  constructor({
    crossbarZInset = defaultOptions.crossbarZInset,
    length = defaultOptions.length,
    position = {},
    rotationY = defaultOptions.rotationY,
    sideRailEndInset = defaultOptions.sideRailEndInset,
    sideRailXInset = defaultOptions.sideRailXInset,
    sideRailYOffset = defaultOptions.sideRailYOffset,
    wheelXOutset = defaultOptions.wheelXOutset,
    width = defaultOptions.width
  }: DoubleChainConveyorOptions = {}) {
    super();
    this.crossbarZInset = validateNonNegativeNumber(crossbarZInset, "crossbarZInset");
    this.length = validatePositiveNumber(length, "length");
    this.width = validatePositiveNumber(width, "width");
    this.sideRailEndInset = validateNonNegativeNumber(sideRailEndInset, "sideRailEndInset");
    this.sideRailXInset = validateNonNegativeNumber(sideRailXInset, "sideRailXInset");
    this.wheelXOutset = validateNonNegativeNumber(wheelXOutset, "wheelXOutset");
    this.requestedSideRailYOffset =
      sideRailYOffset === undefined
        ? undefined
        : validateNonNegativeNumber(sideRailYOffset, "sideRailYOffset");
    if (!Number.isFinite(rotationY)) {
      throw new RangeError("rotationY must be finite");
    }

    this.name = "double-chain-conveyor";
    this.structure.name = "double-chain-conveyor-structure";
    this.position.set(position.x ?? 0, position.y ?? 0, position.z ?? 0);
    this.rotation.y = rotationY;
    this.add(this.structure);
  }

  static async create(
    options: DoubleChainConveyorOptions = {},
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    const conveyor = new DoubleChainConveyor(options);
    await conveyor.loadStructure(loader);
    return conveyor;
  }

  getSideRailYOffset() {
    return this.sideRailYOffset;
  }

  getCrossbarZInset() {
    return this.crossbarZInset;
  }

  getWheelXOutset() {
    return this.wheelXOutset;
  }

  setWheelXOutset(xOutset: number) {
    this.wheelXOutset = validateNonNegativeNumber(xOutset, "wheelXOutset");
    const halfWheelSpanX = this.length / 2 - this.wheelHalfWidth + this.wheelXOutset;
    this.structure.children
      .filter(({ name }) => name.startsWith("double-chain-conveyor-wheel-"))
      .forEach((wheel) => {
        wheel.position.x = (wheel.name.endsWith("-left") ? -1 : 1) * halfWheelSpanX;
      });
  }

  setCrossbarZInset(zInset: number) {
    this.crossbarZInset = validateNonNegativeNumber(zInset, "crossbarZInset");
    this.structure.children
      .filter(
        ({ name }) =>
          name.startsWith("double-chain-conveyor-crossbar-") ||
          name.startsWith("double-chain-conveyor-wheel-")
      )
      .forEach((part) => {
        const direction = part.name.includes("-back") ? -1 : 1;
        part.position.z = direction * (this.crossbarBaseZ - this.crossbarZInset);
      });
  }

  getSideRailEndInset() {
    return this.sideRailEndInset;
  }

  setSideRailEndInset(endInset: number) {
    this.sideRailEndInset = validateNonNegativeNumber(endInset, "sideRailEndInset");
    if (this.sideRailOriginalLength <= 0) {
      return;
    }
    const scaleZ =
      Math.max(0, this.width - this.sideRailEndInset * 2) / this.sideRailOriginalLength;
    this.structure.children
      .filter(({ name }) => name.startsWith("double-chain-conveyor-side-rail-"))
      .forEach((sideRail) => {
        sideRail.children[0]?.scale.setZ(scaleZ);
      });
  }

  getSideRailXInset() {
    return this.sideRailXInset;
  }

  setSideRailXInset(xInset: number) {
    this.sideRailXInset = validateNonNegativeNumber(xInset, "sideRailXInset");
    this.structure.children
      .filter(({ name }) => name.startsWith("double-chain-conveyor-side-rail-"))
      .forEach((sideRail) => {
        const direction = sideRail.name.endsWith("-left") ? -1 : 1;
        sideRail.position.x = direction * (this.sideRailBaseX - this.sideRailXInset);
      });
  }

  setSideRailYOffset(yOffset: number) {
    this.sideRailYOffset = validateNonNegativeNumber(yOffset, "sideRailYOffset");
    this.structure.children
      .filter(({ name }) => name.startsWith("double-chain-conveyor-side-rail-"))
      .forEach((sideRail) => {
        sideRail.position.y = this.sideRailYOffset;
      });
  }

  private async loadStructure(loader: ModelAssetLoader) {
    const [
      backLeft,
      backRight,
      frontLeft,
      frontRight,
      backCrossbar,
      frontCrossbar,
      backLeftWheel,
      backRightWheel,
      frontLeftWheel,
      frontRightWheel,
      leftRail,
      rightRail
    ] = await Promise.all([
      loadAlignedPart(
        loader,
        ModelAsset.DoubleChainConveyorPost,
        "double-chain-conveyor-post-back-left",
        "bottom"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.DoubleChainConveyorPost,
        "double-chain-conveyor-post-back-right",
        "bottom"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.DoubleChainConveyorPost,
        "double-chain-conveyor-post-front-left",
        "bottom"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.DoubleChainConveyorPost,
        "double-chain-conveyor-post-front-right",
        "bottom"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.DoubleChainConveyorCrossbar,
        "double-chain-conveyor-crossbar-back",
        "center",
        "双链输送机-侧面"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.DoubleChainConveyorCrossbar,
        "double-chain-conveyor-crossbar-front",
        "center",
        "双链输送机-侧面"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.DoubleChainConveyorWheel,
        "double-chain-conveyor-wheel-back-left",
        "center",
        "Rectangle012"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.DoubleChainConveyorWheel,
        "double-chain-conveyor-wheel-back-right",
        "center",
        "Rectangle012"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.DoubleChainConveyorWheel,
        "double-chain-conveyor-wheel-front-left",
        "center",
        "Rectangle012"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.DoubleChainConveyorWheel,
        "double-chain-conveyor-wheel-front-right",
        "center",
        "Rectangle012"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.DoubleChainConveyorSideRail,
        "double-chain-conveyor-side-rail-left",
        "center"
      ),
      loadAlignedPart(
        loader,
        ModelAsset.DoubleChainConveyorSideRail,
        "double-chain-conveyor-side-rail-right",
        "center"
      )
    ]);
    const posts = [backLeft, backRight, frontLeft, frontRight];
    const postWidth = backLeft.size.x;
    const postDepth = backLeft.size.z;
    const halfPostSpanX = Math.max(0, this.length / 2 - postWidth / 2);
    const halfPostSpanZ = Math.max(0, this.width / 2 - postDepth / 2);
    const postPlacements = [
      { part: backLeft, rotationY: backPostRotationY, x: -halfPostSpanX, z: -halfPostSpanZ },
      { part: backRight, rotationY: backPostRotationY, x: halfPostSpanX, z: -halfPostSpanZ },
      { part: frontLeft, rotationY: frontPostRotationY, x: -halfPostSpanX, z: halfPostSpanZ },
      { part: frontRight, rotationY: frontPostRotationY, x: halfPostSpanX, z: halfPostSpanZ }
    ];
    postPlacements.forEach(({ part, rotationY, x, z }) => {
      part.object.position.set(x, 0, z);
      part.content.rotation.y = rotationY;
    });

    this.sideRailYOffset = this.requestedSideRailYOffset ?? backLeft.size.y - leftRail.size.y / 2;
    this.crossbarBaseZ = Math.max(0, this.width / 2 - backCrossbar.size.z / 2);
    const crossbarCenterY = backLeft.size.y + backCrossbar.size.y / 2;
    const crossbarScaleX = this.length / backCrossbar.size.x;
    [backCrossbar, frontCrossbar].forEach((crossbar, index) => {
      const isBack = index === 0;
      crossbar.object.position.set(
        0,
        crossbarCenterY,
        (isBack ? -1 : 1) * (this.crossbarBaseZ - this.crossbarZInset)
      );
      crossbar.content.rotation.y = isBack ? 0 : Math.PI;
      crossbar.content.scale.x = crossbarScaleX;
    });
    const wheelCenterY = crossbarCenterY + backCrossbar.size.y / 2 - backLeftWheel.size.y / 2;
    this.wheelHalfWidth = backLeftWheel.size.x / 2;
    const halfWheelSpanX = this.length / 2 - this.wheelHalfWidth + this.wheelXOutset;
    const wheelPlacements = [
      { part: backLeftWheel, isBack: true, isLeft: true },
      { part: backRightWheel, isBack: true, isLeft: false },
      { part: frontLeftWheel, isBack: false, isLeft: true },
      { part: frontRightWheel, isBack: false, isLeft: false }
    ];
    wheelPlacements.forEach(({ part, isBack, isLeft }) => {
      part.object.position.set(
        isLeft ? -halfWheelSpanX : halfWheelSpanX,
        wheelCenterY,
        (isBack ? -1 : 1) * (this.crossbarBaseZ - this.crossbarZInset)
      );
      part.content.rotation.y = isLeft ? 0 : Math.PI;
    });

    this.sideRailBaseX = this.length / 2 + leftRail.size.x / 2;
    this.sideRailOriginalLength = leftRail.size.z;
    const sideRailScaleZ =
      Math.max(0, this.width - this.sideRailEndInset * 2) / this.sideRailOriginalLength;
    [leftRail, rightRail].forEach((rail, index) => {
      const isLeft = index === 0;
      const direction = isLeft ? -1 : 1;
      rail.object.position.set(
        direction * (this.sideRailBaseX - this.sideRailXInset),
        this.sideRailYOffset,
        0
      );
      rail.content.rotation.y = isLeft ? 0 : Math.PI;
      rail.content.scale.z = sideRailScaleZ;
    });

    this.structure.add(
      ...posts.map(({ object }) => object),
      backCrossbar.object,
      frontCrossbar.object,
      ...wheelPlacements.map(({ part }) => part.object),
      leftRail.object,
      rightRail.object
    );
  }
}

async function loadAlignedPart(
  loader: ModelAssetLoader,
  asset: ModelAsset,
  name: string,
  origin: "bottom" | "center",
  childName?: string
): Promise<AlignedPart> {
  const loadedModel = await loader.load(asset);
  loadedModel.updateWorldMatrix(true, true);
  const child = childName ? loadedModel.getObjectByName(childName) : undefined;
  const model = child ?? loadedModel;
  if (child) {
    const worldMatrix = child.matrixWorld.clone();
    child.removeFromParent();
    worldMatrix.decompose(child.position, child.quaternion, child.scale);
  }
  model.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(model);
  const center = bounds.getCenter(new Vector3());
  const size = bounds.getSize(new Vector3());
  const object = new Group();
  const content = new Group();

  model.position.set(
    model.position.x - center.x,
    model.position.y - (origin === "bottom" ? bounds.min.y : center.y),
    model.position.z - center.z
  );
  object.name = name;
  content.add(model);
  object.add(content);
  return { content, object, size };
}

function validatePositiveNumber(value: number, label: string) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${label} must be a positive finite number`);
  }
  return value;
}

function validateNonNegativeNumber(value: number, label: string) {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${label} must be a non-negative finite number`);
  }
  return value;
}
